import { useEffect, useMemo, useState } from 'react';
import {
    analyticsApi,
    buildTableQuery,
    type AnalyticsEvent,
    type AnalyticsFeatures,
    type AnalyticsFilters,
    type FeatureTrendPoint,
} from '../../services/analytics';
import { AccountTableCard } from './AccountTableCard';
import {
    MOST_USED_COLUMNS,
    MOST_USED_FORMATTERS,
    MOST_USED_SORT,
    MOVEMENTS_COLUMNS,
    MOVEMENTS_FORMATTERS,
} from './accountTables';
import { DEFAULT_EVENT_ID, eventOptions } from './events';
import { EventSelect } from './EventSelect';
import { FeatureFlags } from './FeatureFlags';
import { FeatureUsageByCustomer } from './FeatureUsageByCustomer';
import { buildFeaturesCards } from './featuresCards';
import { InfoTip } from './InfoTip';
import { MetricCard } from './MetricCard';
import { METRIC_INFO } from './metricInfo';
import { TrendChart, type TrendMetric } from './TrendChart';
import styles from './FeaturesPanel.module.css';

type FeatureMetric = 'acoesDoEvento' | 'contasQueUsaram' | 'lancamentos';

const FEATURE_METRICS: TrendMetric<FeatureMetric>[] = [
    { key: 'acoesDoEvento', label: 'Ações do evento', unit: 'int' },
    { key: 'contasQueUsaram', label: 'Contas que usaram', unit: 'int' },
    { key: 'lancamentos', label: 'Lançamentos', unit: 'int' },
];

const USAGE_DEFAULT_EVENT_ID = 'lancamento';

interface FeaturesPanelProps {
    filters: AnalyticsFilters;
    /** Falso enquanto o periodo personalizado esta incompleto: nada e consultado. */
    enabled: boolean;
    /** Muda quando a pessoa pede "tentar novamente" no painel. */
    refreshKey: number;
    onRetry: () => void;
    timeZone: string;
    /** Eventos do seletor de cada grafico, vindos de meta. */
    events?: AnalyticsEvent[];
}

interface Settled {
    key: string;
    error: string | null;
}

export function FeaturesPanel({ filters, enabled, refreshKey, onRetry, timeZone, events }: FeaturesPanelProps) {
    const [features, setFeatures] = useState<AnalyticsFeatures | null>(null);
    const [settled, setSettled] = useState<Settled | null>(null);
    const [trendEvent, setTrendEvent] = useState(DEFAULT_EVENT_ID);
    const [metric, setMetric] = useState<FeatureMetric>('acoesDoEvento');
    const [usageEvent, setUsageEvent] = useState(USAGE_DEFAULT_EVENT_ID);

    const requestKey = enabled ? `${buildTableQuery(filters, { event: trendEvent })}#${refreshKey}` : null;

    useEffect(() => {
        if (!requestKey) return;
        const controller = new AbortController();
        analyticsApi
            .features(filters, trendEvent, controller.signal)
            .then((data) => {
                setFeatures(data);
                setSettled({ key: requestKey, error: null });
            })
            .catch((e: unknown) => {
                if (controller.signal.aborted) return;
                setSettled({
                    key: requestKey,
                    error: e instanceof Error ? e.message : 'Não foi possível carregar o painel.',
                });
            });
        return () => controller.abort();
        // requestKey resume filtros, evento do grafico e contador de nova tentativa.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    const loading = requestKey !== null && settled?.key !== requestKey;
    const error = settled && settled.key === requestKey ? settled.error : null;

    const cards = useMemo(() => (features ? buildFeaturesCards(features.cards) : null), [features]);
    const options = useMemo(() => eventOptions(events ?? features?.eventos), [events, features]);

    return (
        <>
            {!enabled && <p className={styles.notice}>Informe as duas datas do período personalizado.</p>}

            {error && (
                <div className={styles.error} role="alert">
                    <span>{error}</span>
                    <button type="button" className={styles.retry} onClick={onRetry}>
                        Tentar novamente
                    </button>
                </div>
            )}

            {loading && !features && (
                <div className={styles.loadingState} aria-live="polite">
                    Carregando...
                </div>
            )}

            {features && cards && (
                <div className={`${styles.content} ${loading ? styles.contentLoading : ''}`} aria-busy={loading}>
                    <div className={styles.cardsGrid}>
                        {cards.map((c, i) => (
                            <MetricCard
                                key={c.id}
                                infoKey={c.id}
                                title={c.title}
                                value={c.value}
                                caption={c.caption}
                                tipAlign={i === 0 ? 'start' : 'end'}
                            />
                        ))}
                    </div>

                    <TrendChart<FeatureMetric, FeatureTrendPoint>
                        title="Tendência de funcionalidades"
                        infoKey="tendenciaFuncionalidades"
                        points={features.trend.points}
                        granularity={features.trend.granularity}
                        endDay={features.period.to}
                        metrics={FEATURE_METRICS}
                        metric={metric}
                        onMetricChange={setMetric}
                        getValue={(p, m) => p[m]}
                        loading={loading}
                        controls={
                            <EventSelect
                                label="Evento neste gráfico"
                                value={trendEvent}
                                options={options}
                                onChange={setTrendEvent}
                            />
                        }
                    />

                    <FeatureFlags flags={features.featureFlags} />

                    <AccountTableCard
                        name="movimentacoes-por-cliente"
                        title={
                            <>
                                Movimentações por cliente
                                <InfoTip info={METRIC_INFO.movimentacoesPorCliente} label="Movimentações por cliente" align="start" />
                            </>
                        }
                        columns={MOVEMENTS_COLUMNS}
                        formatters={MOVEMENTS_FORMATTERS}
                        exportFilename="analytics-movimentacoes-por-cliente.xlsx"
                        emptyMessage="Nenhuma movimentação no período."
                        filters={filters}
                        enabled={enabled}
                        refreshKey={refreshKey}
                        timeZone={timeZone}
                    />

                    <AccountTableCard
                        name="funcionalidades-mais-utilizadas"
                        title={
                            <>
                                Funcionalidades mais utilizadas
                                <InfoTip info={METRIC_INFO.maisUtilizadas} label="Funcionalidades mais utilizadas" align="start" />
                            </>
                        }
                        columns={MOST_USED_COLUMNS}
                        formatters={MOST_USED_FORMATTERS}
                        defaultSort={MOST_USED_SORT}
                        exportFilename="analytics-funcionalidades-mais-utilizadas.xlsx"
                        emptyMessage="Nenhuma ação registrada no período."
                        filters={filters}
                        enabled={enabled}
                        refreshKey={refreshKey}
                        timeZone={timeZone}
                    />

                    <FeatureUsageByCustomer
                        events={options}
                        event={usageEvent}
                        onEventChange={setUsageEvent}
                        filters={filters}
                        enabled={enabled}
                        refreshKey={refreshKey}
                    />
                </div>
            )}
        </>
    );
}
